import React, {FC, useEffect, useState} from 'react'
import {Modal, Button, Spinner} from 'react-bootstrap'
import {
  getSettingsOfSelectedPlatformAsyncNew,
} from 'src/redux/actions/platformSettingActions'
import Loader from 'src/utils/Loader'
import {getPlatformOwnerFieldsAsync} from 'src/redux/actions/platformActions'
import {saveFieldMappingAsync} from 'src/redux/actions/productActions'
import {useAppDispatch} from 'src/redux/storeHooks'
import useAppToast from 'src/hooks/useAppToast'

interface Props {
  show: boolean
  platformData: any
  onSuccess: () => void
}

const ZohoOrgSelectionModal: FC<Props> = ({show, platformData, onSuccess}) => {
  const dispatch = useAppDispatch()
  const {errorToast} = useAppToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [orgFieldId, setOrgFieldId] = useState<number | null>(null)
  const [orgOptions, setOrgOptions] = useState<any[]>([])
  const [selectedOrg, setSelectedOrg] = useState<any>(null)

  useEffect(() => {
    if (!platformData) return

    // Step 1: fetch settings to get org options (platformfielddata_set)
    // Step 2: fetch platform owner fields to get the destination_field_id needed by saveFieldMappingAsync
    Promise.all([
      dispatch(
        getSettingsOfSelectedPlatformAsyncNew({
          availablePlatformId: String(platformData.platform_info?.id),
          platform_id: String(platformData.id),
          type: 'MAIN_STORE_SETTING',
          primaryPlatformId: 0,
          erp_flow: true,
        })
      ),
      dispatch(
        getPlatformOwnerFieldsAsync({
          platform_id: String(platformData.id),
          field_id: '',
        })
      ),
    ]).then(([settingsRes, ownerFieldsRes]: [any, any]) => {
      // Extract ORGANIZATION mapping from settings
      const settingsArray: any[] = settingsRes?.payload?.data ?? []
      const allMappings = settingsArray.flatMap((s: any) => s.mappings_data ?? [])
      const orgMappingFromSettings = allMappings.find(
        (m: any) => m.api_code === 'ORGANIZATION' && m.owner === 'PLATFORM'
      )

      // Extract ORGANIZATION field definition from platform owner fields
      const ownerFields: any[] = ownerFieldsRes?.payload?.data ?? []
      const orgOwnerField = ownerFields.find(
        (f: any) => f.api_code === 'ORGANIZATION' || f.field_code === 'ORGANIZATION'
      )

      const options = orgMappingFromSettings?.platformfielddata_set ?? []

      if (!options.length || !orgOwnerField) {
        // No org options or no owner field definition — skip popup
        onSuccess()
        return
      }

      setOrgFieldId(orgOwnerField.id)
      setOrgOptions(options)
      if (options.length === 1) {
        setSelectedOrg(options[0])
      }
      setLoading(false)
    })
  }, [platformData])

  const handleConfirm = () => {
    if (!selectedOrg || !orgFieldId) return
    setSaving(true)
    dispatch(
      saveFieldMappingAsync({
        source_field_id: null,
        destination_field_id: orgFieldId,
        fieldmappingdata_set: [
          {
            destination_platform_data_id: selectedOrg.id,
          },
        ],
      })
    ).then((res: any) => {
      setSaving(false)
      if (res?.meta?.requestStatus === 'fulfilled') {
        onSuccess()
      } else {
        errorToast('Failed to save organization. Please try again.')
      }
    }).catch(() => {
      setSaving(false)
      errorToast('Failed to save organization. Please try again.')
    })
  }

  return (
    <Modal show={show} centered backdrop='static' keyboard={false}>
      <Modal.Header>
        <Modal.Title>Select Zoho Organization</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className='d-flex justify-content-center py-4'>
            <Loader variant="section" />
          </div>
        ) : (
          <>
            <p className='text-muted mb-3'>
              Please select the Zoho organization you want to connect to Commercium.
            </p>
            <select
              className='form-select'
              value={selectedOrg?.id ?? ''}
              onChange={(e) => {
                const org = orgOptions.find((o: any) => String(o.id) === e.target.value)
                setSelectedOrg(org ?? null)
              }}
            >
              <option value=''>-- Select Organization --</option>
              {orgOptions.map((org: any) => (
                <option key={org.id} value={org.id}>
                  {org.api_name}
                </option>
              ))}
            </select>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='primary'
          disabled={!selectedOrg || saving || loading}
          onClick={handleConfirm}
        >
          {saving ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              Saving...
            </>
          ) : (
            'Confirm'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ZohoOrgSelectionModal
